import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import api from '../../hooks/useApi';

// ---- Sidebar ----
const NAV = [
  { to: '/admin', label: 'Dashboard', icon: '📊', exact: true },
  { to: '/admin/projects', label: 'Projects', icon: '🏗️' },
  { to: '/admin/videos', label: 'Videos', icon: '🎬' },
  { to: '/admin/feedback', label: 'Feedback', icon: '💬' },
  { to: '/admin/enquiries', label: 'Enquiries', icon: '📩' },
];

function Sidebar() {
  const { pathname } = useLocation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div style={{ width: '220px', background: '#0A0A0A', borderRight: '1px solid rgba(201,168,76,0.12)', display: 'flex', flexDirection: 'column', minHeight: '100vh', flexShrink: 0 }}>
      <div style={{ padding: '1.75rem 1.5rem', borderBottom: '1px solid rgba(201,168,76,0.1)' }}>
        <div style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: '1.2rem', color: '#C9A84C' }}>NirmalRam</div>
        <div style={{ fontSize: '0.65rem', color: '#5A5450', letterSpacing: '2px', textTransform: 'uppercase', marginTop: '2px' }}>Admin Panel</div>
      </div>
      <nav style={{ flex: 1, padding: '1rem 0' }}>
        {NAV.map(n => {
          const active = n.exact ? pathname === n.to : pathname.startsWith(n.to) && n.to !== '/admin';
          const isExact = n.to === '/admin' && pathname === '/admin';
          const isActive = isExact || (!n.exact && pathname.startsWith(n.to));
          return (
            <Link key={n.to} to={n.to}
              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1.5rem', fontSize: '0.88rem', color: isActive ? '#C9A84C' : '#5A5450', background: isActive ? 'rgba(201,168,76,0.06)' : 'transparent', borderLeft: isActive ? '2px solid #C9A84C' : '2px solid transparent', textDecoration: 'none', transition: 'all 0.2s' }}>
              <span>{n.icon}</span>{n.label}
            </Link>
          );
        })}
      </nav>
      <div style={{ padding: '1.25rem 1.5rem', borderTop: '1px solid rgba(201,168,76,0.1)' }}>
        <div style={{ fontSize: '0.8rem', color: '#5A5450', marginBottom: '0.5rem' }}>{user?.name}</div>
        <button onClick={handleLogout} style={{ background: 'none', border: '1px solid rgba(201,168,76,0.2)', color: '#8A8070', padding: '0.4rem 0.9rem', borderRadius: '2px', fontSize: '0.78rem', cursor: 'pointer', width: '100%', fontFamily: 'Outfit,sans-serif' }}>
          Sign Out
        </button>
      </div>
    </div>
  );
}

// ---- Dashboard Home ----
function DashHome() {
  const [counts, setCounts] = useState({ projects: 0, feedback: 0, enquiries: 0, videos: 0 });
  useEffect(() => {
    Promise.allSettled([api.getProjects(), api.getAllFeedback(), api.getEnquiries(), api.getVideos()])
      .then(([p, f, e, v]) => setCounts({
        projects: p.value?.data?.length || 0,
        feedback: f.value?.data?.length || 0,
        enquiries: e.value?.data?.length || 0,
        videos: v.value?.data?.length || 0,
      }));
  }, []);

  return (
    <div>
      <h2 style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: '1.8rem', marginBottom: '2rem' }}>Dashboard</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '1rem', marginBottom: '3rem' }}>
        {[['🏗️', 'Projects', counts.projects, '#C9A84C'], ['💬', 'Reviews', counts.feedback, '#60a5fa'], ['📩', 'Enquiries', counts.enquiries, '#4ade80'], ['🎬', 'Videos', counts.videos, '#f472b6']].map(([icon, label, val, color]) => (
          <div key={label} style={{ background: '#111', border: '1px solid rgba(201,168,76,0.1)', padding: '1.5rem', borderRadius: '3px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{icon}</div>
            <div style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: '2.2rem', color }}>{val}</div>
            <div style={{ fontSize: '0.75rem', color: '#5A5450', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '1rem' }}>
        {[['Add New Project', '/admin/projects', '🏗️'], ['Upload Video', '/admin/videos', '🎬'], ['View Enquiries', '/admin/enquiries', '📩'], ['Moderate Reviews', '/admin/feedback', '💬']].map(([label, to, icon]) => (
          <Link key={to} to={to} style={{ background: '#0E0E0E', border: '1px solid rgba(201,168,76,0.1)', padding: '1.25rem', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '0.75rem', textDecoration: 'none', transition: 'border-color 0.2s', color: '#F0EAE0', fontSize: '0.9rem' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(201,168,76,0.4)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(201,168,76,0.1)'}>
            <span style={{ fontSize: '1.2rem' }}>{icon}</span>{label}
          </Link>
        ))}
      </div>
    </div>
  );
}

// ---- Projects Admin ----
function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ title: '', location: '', type: 'Residential', status: 'Completed', year: new Date().getFullYear(), area: '', budget: '', description: '', features: '', isFeatured: false });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list');
  const fileRef = useRef();

  const load = () => api.getProjects().then(r => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!form.title) return toast.error('Title required');
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach(f => fd.append('images', f));
      await api.createProject(fd);
      toast.success('Project added!');
      setForm({ title: '', location: '', type: 'Residential', status: 'Completed', year: new Date().getFullYear(), area: '', budget: '', description: '', features: '', isFeatured: false });
      setFiles([]);
      load();
      setView('list');
    } catch (err) { toast.error(err?.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    await api.deleteProject(id);
    toast.success('Deleted');
    load();
  };

  const upd = k => e => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: '1.8rem' }}>Projects</h2>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn-ghost" onClick={() => setView('list')} style={{ borderColor: view === 'list' ? '#C9A84C' : undefined }}>List</button>
          <button className="btn-gold" onClick={() => setView('add')}>+ Add Project</button>
        </div>
      </div>

      {view === 'add' && (
        <div style={{ background: '#111', border: '1px solid rgba(201,168,76,0.15)', padding: '2rem', borderRadius: '3px', marginBottom: '2rem' }}>
          <h3 style={{ fontFamily: 'Cormorant Garamond,serif', marginBottom: '1.5rem' }}>Add New Project</h3>
          <div className="form-row">
            <div className="form-group"><label>Project Title *</label><input value={form.title} onChange={upd('title')} placeholder="e.g. 2 BHK Dream Home" /></div>
            <div className="form-group"><label>Location</label><input value={form.location} onChange={upd('location')} placeholder="e.g. Nandurbar" /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Type</label>
              <select value={form.type} onChange={upd('type')}>{['Residential','Commercial','Government','Renovation','Industrial'].map(t => <option key={t}>{t}</option>)}</select>
            </div>
            <div className="form-group"><label>Status</label>
              <select value={form.status} onChange={upd('status')}>{['Completed','In Progress','Upcoming'].map(s => <option key={s}>{s}</option>)}</select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Year</label><input type="number" value={form.year} onChange={upd('year')} /></div>
            <div className="form-group"><label>Area</label><input value={form.area} onChange={upd('area')} placeholder="e.g. 1,400 sq ft" /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Budget (optional)</label><input value={form.budget} onChange={upd('budget')} placeholder="e.g. ₹25 Lakhs" /></div>
            <div className="form-group"><label>Client Name (optional)</label><input value={form.client} onChange={e => setForm({...form, client: e.target.value})} placeholder="e.g. Suresh Patil" /></div>
          </div>
          <div className="form-group"><label>Description</label><textarea value={form.description} onChange={upd('description')} placeholder="Brief project description..." /></div>
          <div className="form-group"><label>Key Features (comma separated)</label><input value={form.features} onChange={upd('features')} placeholder="e.g. RCC Frame, Premium Tiles, Vastu Layout" /></div>
          <div className="form-group">
            <label>Project Images</label>
            <input type="file" ref={fileRef} multiple accept="image/*" onChange={e => setFiles(Array.from(e.target.files))} style={{ color: '#8A8070' }} />
            {files.length > 0 && <p style={{ color: '#C9A84C', fontSize: '0.82rem', marginTop: '0.5rem' }}>{files.length} image(s) selected</p>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <input type="checkbox" id="featured" checked={form.isFeatured} onChange={e => setForm({...form, isFeatured: e.target.checked})} style={{ width: '16px', height: '16px' }} />
            <label htmlFor="featured" style={{ color: '#8A8070', fontSize: '0.9rem', cursor: 'pointer' }}>Feature on homepage</label>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button className="btn-gold" onClick={handleAdd} disabled={loading}>{loading ? 'Saving...' : 'Save Project'}</button>
            <button className="btn-ghost" onClick={() => setView('list')}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {projects.map(p => (
          <div key={p._id} style={{ background: '#0E0E0E', border: '1px solid rgba(201,168,76,0.1)', padding: '1.25rem 1.5rem', borderRadius: '3px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              {p.images?.[0]?.url && <img src={p.images[0].url} alt="" style={{ width: '52px', height: '42px', objectFit: 'cover', borderRadius: '2px', border: '1px solid rgba(201,168,76,0.15)' }} />}
              <div>
                <div style={{ fontWeight: 500, marginBottom: '0.2rem' }}>{p.title}</div>
                <div style={{ fontSize: '0.8rem', color: '#5A5450' }}>{p.type} · {p.location} · {p.year}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span className={`badge ${p.status === 'Completed' ? 'badge-green' : 'badge-gold'}`}>{p.status}</span>
              {p.isFeatured && <span className="badge badge-blue">Featured</span>}
              <button onClick={() => handleDelete(p._id)}
                style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', padding: '0.35rem 0.9rem', borderRadius: '2px', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'Outfit,sans-serif' }}>
                Delete
              </button>
            </div>
          </div>
        ))}
        {projects.length === 0 && <p style={{ color: '#5A5450', padding: '2rem', textAlign: 'center' }}>No projects yet. Add your first project!</p>}
      </div>
    </div>
  );
}

// ---- Videos Admin ----
function AdminVideos() {
  const [videos, setVideos] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', url: '', type: 'project' });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const load = () => api.getVideos().then(r => setVideos(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!form.title || (!form.url && !file)) return toast.error('Title and URL or file required');
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('video', file);
      await api.uploadVideo(fd);
      toast.success('Video added!');
      setForm({ title: '', description: '', url: '', type: 'project' });
      setFile(null); load(); setShow(false);
    } catch (err) { toast.error(err?.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: '1.8rem' }}>Videos</h2>
        <button className="btn-gold" onClick={() => setShow(!show)}>+ Add Video</button>
      </div>
      {show && (
        <div style={{ background: '#111', border: '1px solid rgba(201,168,76,0.15)', padding: '2rem', borderRadius: '3px', marginBottom: '2rem' }}>
          <div className="form-group"><label>Title *</label><input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Video title" /></div>
          <div className="form-group"><label>YouTube / Video URL</label><input value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder="https://youtube.com/watch?v=..." /></div>
          <div className="form-group"><label>Or Upload Video File</label><input type="file" accept="video/*" onChange={e => setFile(e.target.files[0])} style={{ color: '#8A8070' }} /></div>
          <div className="form-row">
            <div className="form-group"><label>Type</label>
              <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}>{['project','process','testimonial','timelapse'].map(t => <option key={t}>{t}</option>)}</select>
            </div>
          </div>
          <div className="form-group"><label>Description</label><textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Brief description..." /></div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button className="btn-gold" onClick={handleAdd} disabled={loading}>{loading ? 'Saving...' : 'Save Video'}</button>
            <button className="btn-ghost" onClick={() => setShow(false)}>Cancel</button>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {videos.map(v => (
          <div key={v._id} style={{ background: '#0E0E0E', border: '1px solid rgba(201,168,76,0.1)', padding: '1.25rem 1.5rem', borderRadius: '3px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <div style={{ fontWeight: 500, marginBottom: '0.2rem' }}>{v.title}</div>
              <div style={{ fontSize: '0.8rem', color: '#5A5450' }}>{v.type} · {v.url?.slice(0, 50)}...</div>
            </div>
            <button onClick={async () => { await api.deleteVideo(v._id); toast.success('Deleted'); load(); }}
              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', padding: '0.35rem 0.9rem', borderRadius: '2px', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'Outfit,sans-serif' }}>
              Delete
            </button>
          </div>
        ))}
        {videos.length === 0 && <p style={{ color: '#5A5450', padding: '2rem', textAlign: 'center' }}>No videos yet.</p>}
      </div>
    </div>
  );
}

// ---- Feedback Admin ----
function AdminFeedback() {
  const [items, setItems] = useState([]);
  const load = () => api.getAllFeedback().then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (id, field, val) => {
    await api.updateFeedback(id, { [field]: val });
    toast.success('Updated');
    load();
  };

  return (
    <div>
      <h2 style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: '1.8rem', marginBottom: '2rem' }}>Feedback Moderation</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {items.map(f => (
          <div key={f._id} style={{ background: '#0E0E0E', border: '1px solid rgba(201,168,76,0.1)', padding: '1.5rem', borderRadius: '3px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 500 }}>{f.name}</span>
                  <span style={{ fontSize: '0.8rem', color: '#5A5450' }}>📍 {f.location}</span>
                  <span style={{ color: '#C9A84C', fontSize: '0.85rem' }}>{'★'.repeat(f.rating)}</span>
                </div>
                <p style={{ color: '#8A8070', fontSize: '0.9rem', lineHeight: 1.6 }}>{f.text}</p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button onClick={() => toggle(f._id, 'isPublished', !f.isPublished)}
                  className={`badge ${f.isPublished ? 'badge-green' : 'badge-gold'}`}
                  style={{ cursor: 'pointer', border: 'none', fontFamily: 'Outfit,sans-serif' }}>
                  {f.isPublished ? 'Published' : 'Publish'}
                </button>
                <button onClick={async () => { await api.deleteFeedback(f._id); toast.success('Deleted'); load(); }}
                  style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', padding: '0.3rem 0.75rem', borderRadius: '2px', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'Outfit,sans-serif' }}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p style={{ color: '#5A5450', padding: '2rem', textAlign: 'center' }}>No feedback yet.</p>}
      </div>
    </div>
  );
}

// ---- Enquiries Admin ----
function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const load = () => api.getEnquiries().then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const statusColors = { New: 'badge-gold', Contacted: 'badge-blue', Closed: 'badge-green' };

  return (
    <div>
      <h2 style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: '1.8rem', marginBottom: '2rem' }}>
        Enquiries <span style={{ fontSize: '1rem', color: '#C9A84C' }}>({items.filter(i => i.status === 'New').length} new)</span>
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {items.map(e => (
          <div key={e._id} style={{ background: '#0E0E0E', border: '1px solid rgba(201,168,76,0.1)', padding: '1.5rem', borderRadius: '3px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 500, fontSize: '1rem' }}>{e.name}</span>
                  <a href={`tel:${e.phone}`} style={{ color: '#C9A84C', fontSize: '0.9rem' }}>📞 {e.phone}</a>
                  {e.email && <a href={`mailto:${e.email}`} style={{ color: '#8A8070', fontSize: '0.85rem' }}>✉ {e.email}</a>}
                </div>
                <div style={{ fontSize: '0.82rem', color: '#8A8070', marginBottom: '0.4rem' }}>Service: <strong style={{ color: '#F0EAE0' }}>{e.service}</strong></div>
                {e.message && <p style={{ color: '#5A5450', fontSize: '0.85rem', lineHeight: 1.5 }}>{e.message}</p>}
                <div style={{ fontSize: '0.75rem', color: '#3A3430', marginTop: '0.5rem' }}>{new Date(e.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <select value={e.status}
                  onChange={async ev => { await api.updateEnquiry(e._id, { status: ev.target.value }); toast.success('Status updated'); load(); }}
                  style={{ background: '#111', border: '1px solid rgba(201,168,76,0.2)', color: '#F0EAE0', padding: '0.35rem 0.75rem', borderRadius: '2px', fontSize: '0.8rem', fontFamily: 'Outfit,sans-serif', cursor: 'pointer' }}>
                  <option>New</option><option>Contacted</option><option>Closed</option>
                </select>
                <a href={`https://wa.me/${e.phone?.replace(/\D/g, '')}?text=Hello%20${encodeURIComponent(e.name)}%2C%20this%20is%20NirmalRam%20Construction%20regarding%20your%20enquiry.`}
                  target="_blank" rel="noreferrer"
                  style={{ background: 'rgba(37,211,102,0.12)', border: '1px solid rgba(37,211,102,0.2)', color: '#4ade80', padding: '0.35rem 0.75rem', borderRadius: '2px', fontSize: '0.78rem', textDecoration: 'none' }}>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p style={{ color: '#5A5450', padding: '2rem', textAlign: 'center' }}>No enquiries yet.</p>}
      </div>
    </div>
  );
}

// ---- Main Dashboard Layout ----
export default function AdminDashboard() {
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#080808' }}>
      <Sidebar />
      <div style={{ flex: 1, padding: '2.5rem', overflowY: 'auto' }}>
        <Routes>
          <Route index element={<DashHome />} />
          <Route path="projects" element={<AdminProjects />} />
          <Route path="videos" element={<AdminVideos />} />
          <Route path="feedback" element={<AdminFeedback />} />
          <Route path="enquiries" element={<AdminEnquiries />} />
        </Routes>
      </div>
    </div>
  );
}
